import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold01,
  Italic01,
  Link01,
  List,
  ReverseLeft,
  ReverseRight,
  Underline01,
} from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { addLink } from '../../shared/tiptap/doc-utilities';
import { IconButton } from '../buttons/icon-button';
import { NumberListIcon } from '../icons/number-list-icon';
import { AddLink } from './add-link';
import './editor.css';
import { TextAlignDropdown } from './text-align-dropdown';

type SimpleTiptapEditorProps = {
  content?: Record<string, any>;
  placeholder?: string;
  onChange?: (json: Record<string, any>) => void;
  onBlur?: () => void;
  className?: string;
  editorClassName?: string;
  disabled?: boolean;
  height?: string; // e.g., '300px', '20rem', 'h-64'
};

function SimpleTiptapToolbar(props: { editor: Editor | null }) {
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [selectedLink, setSelectedLink] = useState<{ text: string; link: string } | null>(null);

  useEffect(
    function () {
      if (props.editor == null || !isAddingLink || typeof window?.getSelection !== 'function') {
        return;
      }
      const selection = window.getSelection();
      if (selection == null) {
        setSelectedLink(null);
        return;
      }
      setSelectedLink({
        text: selection.toString(),
        link: props.editor.getAttributes('link').href as string,
      });
    },
    [isAddingLink, props.editor]
  );

  if (props.editor == null) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-0.5 border-b border-gray-200 bg-gray-50 p-2">
        {/* Bold */}
        <IconButton
          icon={Bold01}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Bold"
          size="sm"
          isActive={props.editor.isActive('bold')}
          onClick={function () {
            props.editor?.chain().focus().toggleBold().run();
          }}
        />

        {/* Italic */}
        <IconButton
          icon={Italic01}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Italic"
          size="sm"
          isActive={props.editor.isActive('italic')}
          onClick={function () {
            props.editor?.chain().focus().toggleItalic().run();
          }}
        />

        {/* Underline */}
        <IconButton
          icon={Underline01}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Underline"
          size="sm"
          isActive={props.editor.isActive('underline')}
          onClick={function () {
            props.editor?.chain().focus().toggleUnderline().run();
          }}
        />

        <div className="mx-1 h-6 w-px bg-gray-300" />

        {/* Bullet List */}
        <IconButton
          icon={List}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Bullet List"
          size="sm"
          isActive={props.editor.isActive('bulletList')}
          onClick={function () {
            props.editor?.chain().focus().toggleBulletList().run();
          }}
        />

        {/* Numbered List */}
        <IconButton
          icon={NumberListIcon}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Numbered List"
          size="sm"
          isActive={props.editor.isActive('orderedList')}
          onClick={function () {
            props.editor?.chain().focus().toggleOrderedList().run();
          }}
        />

        <div className="mx-1 h-6 w-px bg-gray-300" />

        {/* Text Align */}
        <TextAlignDropdown
          onSelectAlignLeft={function () {
            props.editor?.chain().focus().setTextAlign('left').run();
          }}
          onSelectAlignRight={function () {
            props.editor?.chain().focus().setTextAlign('right').run();
          }}
          onSelectAlignCenter={function () {
            props.editor?.chain().focus().setTextAlign('center').run();
          }}
          onSelectJustify={function () {
            props.editor?.chain().focus().setTextAlign('justify').run();
          }}
        />

        {/* Link */}
        <IconButton
          icon={Link01}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Link"
          size="sm"
          isActive={props.editor.isActive('link')}
          onClick={function () {
            setIsAddingLink(true);
          }}
        />

        <div className="mx-1 h-6 w-px bg-gray-300" />

        {/* Undo */}
        <IconButton
          icon={ReverseLeft}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Undo"
          size="sm"
          disabled={!props.editor.can().undo()}
          onClick={function () {
            props.editor?.chain().focus().undo().run();
          }}
        />

        {/* Redo */}
        <IconButton
          icon={ReverseRight}
          className="text-black"
          variant="ghost-neutral"
          aria-label="Redo"
          size="sm"
          disabled={!props.editor.can().redo()}
          onClick={function () {
            props.editor?.chain().focus().redo().run();
          }}
        />
      </div>

      {/* Link Dialog */}
      <AddLink
        key={selectedLink == null ? 'add-link' : selectedLink.text}
        open={isAddingLink}
        onOpenChange={function (isOpen) {
          setIsAddingLink(isOpen);
        }}
        onConfirm={function (text: string | null, link: string, isTargetBlank: boolean) {
          if (props.editor != null) {
            addLink(props.editor, text, link, isTargetBlank);
          }
          setIsAddingLink(false);
        }}
        selectedLink={selectedLink}
        onUnsetLink={function () {
          if (props.editor != null) {
            props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
          }
          setIsAddingLink(false);
        }}
      />
    </>
  );
}

export function SimpleTiptapEditor(props: SimpleTiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    editorProps: {
      attributes: {
        style: 'min-height: 100px;',
        'data-placeholder': props.placeholder ?? '',
      },
    },
    content: props.content ?? '',
    editable: !props.disabled,
    onUpdate: function ({ editor }) {
      if (props.onChange != null) {
        props.onChange(editor.getJSON());
      }
    },
    onBlur: function () {
      if (props.onBlur != null) {
        props.onBlur();
      }
    },
  });

  // Update content when prop changes
  useEffect(
    function () {
      if (editor != null && props.content !== undefined) {
        const currentContent = editor.getJSON();
        if (JSON.stringify(currentContent) !== JSON.stringify(props.content)) {
          editor.commands.setContent(props.content);
        }
      }
    },
    [editor, props.content]
  );

  return (
    <div
      className={twMerge(
        'flex w-full flex-col overflow-hidden rounded-lg border border-gray-300',
        props.className
      )}
      style={props.height != null ? { height: props.height } : undefined}
    >
      <SimpleTiptapToolbar editor={editor} />
      <div
        className={twMerge(
          'simple-tiptap-editor Prose prose-xm max-w-none flex-1 overflow-auto bg-white p-4',
          props.editorClassName,
          props.disabled ? 'cursor-not-allowed opacity-50' : ''
        )}
      >
        <EditorContent editor={editor} className="smallEditor" />
      </div>
    </div>
  );
}
